import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  Typography,
  Box,
  Paper,
  IconButton,
  Divider,
  Grid,
  Stack,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  AccessTime as ClockIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import DateRangeIcon from '@mui/icons-material/DateRange';
import AdminButton from 'components/common/AdminButton';
import MDDatePicker from 'components/MDDatePicker';
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  parseISO,
  subDays,
  subWeeks,
  subMonths,
  differenceInDays
} from 'date-fns';

// Utility for date constraints - you can adjust these as needed
const utils = {
  minDate: subMonths(new Date(), 12), // 12 months ago
  maxDate: new Date() // Today
};

/**
 * Map from external preset keys to internal preset keys
 * @constant {Object}
 */
const PRESET_KEY_MAP = {
  last7days: 'last7Days',
  last30days: 'last30Days',
  yesterday: 'yesterday',
  mtd: 'monthToDate',
  today: 'yesterday', // Map today to yesterday since we don't have a today preset
  custom: 'custom',
};

const DateRangeSelectionModal = ({
  open,
  onClose,
  currentDateRange,
  currentPreset,
  onSubmit,
  minDate,
  maxDate
}) => {
  // Use provided min/max dates or fallback to default
  const dateConstraints = {
    minDate: minDate || utils.minDate,
    maxDate: maxDate || utils.maxDate
  };
  const [dateRange, setDateRange] = useState({
    from: null,
    to: null
  });
  const [selectedPreset, setSelectedPreset] = useState('last7Days');
  const [isLoading, setIsLoading] = useState(false);

  const getPresetRanges = () => {
    const yesterday = subDays(new Date(), 1);
    const today = new Date();

    return {
      yesterday: { from: yesterday, to: yesterday },
      weekToDate: { from: startOfWeek(today, { weekStartsOn: 1 }), to: yesterday },
      last7Days: { from: subDays(yesterday, 6), to: yesterday },
      last30Days: { from: subDays(yesterday, 29), to: yesterday },
      lastWeek: {
        from: startOfWeek(subWeeks(today, 1), { weekStartsOn: 1 }),
        to: endOfWeek(subWeeks(today, 1), { weekStartsOn: 1 }),
      },
      monthToDate: { from: startOfMonth(today), to: yesterday },
      lastMonth: {
        from: startOfMonth(subMonths(today, 1)),
        to: endOfMonth(subMonths(today, 1)),
      },
    };
  };

  // Initialize state when modal opens
  useEffect(() => {
    if (open) {
      const ranges = getPresetRanges();

      // Map external preset key to internal key
      const internalPreset = PRESET_KEY_MAP[currentPreset] || 'custom';

      if (internalPreset !== 'custom' && ranges[internalPreset]) {
        setSelectedPreset(internalPreset);
        setDateRange(ranges[internalPreset]);
      } else if (currentDateRange?.startDate && currentDateRange?.endDate) {
        // Custom date range - parse the dates
        setSelectedPreset('custom');
        setDateRange({
          from: parseISO(currentDateRange.startDate),
          to: parseISO(currentDateRange.endDate)
        });
      } else {
        // Default to last7Days
        setSelectedPreset('last7Days');
        setDateRange(ranges.last7Days);
      }
    }
  }, [open, currentPreset, currentDateRange]);

  const handlePresetSelect = (preset) => {
    setSelectedPreset(preset);
    if (preset !== 'custom') {
      const ranges = getPresetRanges();
      setDateRange(ranges[preset]);
    }
  };

  // const handleFromDateChange = (date) => {
  //   setDateRange(prev => ({
  //     ...prev,
  //     from: date,
  //     to: prev.to && date && date > prev.to ? date : prev.to
  //   }));
  //   setSelectedPreset('custom');
  // };

  // const handleToDateChange = (date) => {
  //   setDateRange(prev => ({
  //     ...prev,
  //     to: date
  //   }));
  //   setSelectedPreset('custom');
  // };

  const handleCustomDateRangeChange = (range) => {
    // range comes from MDDatePicker in format [fromDate, toDate] or single date
    if (Array.isArray(range) && range.length === 2) {
      setDateRange({
        from: range[0],
        to: range[1]
      });
    }
    setSelectedPreset('custom');
  };

  /**
   * Map from internal preset keys back to external preset keys
   * @constant {Object}
   */
  const INTERNAL_TO_EXTERNAL_PRESET = {
    last7Days: 'last7days',
    last30Days: 'last30days',
    yesterday: 'yesterday',
    monthToDate: 'mtd',
    weekToDate: 'custom', // No direct mapping, treat as custom
    lastWeek: 'custom', // No direct mapping, treat as custom
    lastMonth: 'custom', // No direct mapping, treat as custom
    custom: 'custom',
  };

  const handleApply = async () => {
    if (!dateRange.from) return;

    // setIsLoading(true);
    try {
      const selection = {
        from: dateRange.from,
        to: dateRange.to || dateRange.from,
        preset: INTERNAL_TO_EXTERNAL_PRESET[selectedPreset] || 'custom',
      };

      // Call parent's onSubmit which will:
      // 1. Close this modal immediately
      // 2. Start page-level loading
      // 3. Fetch data and update on page
      await onSubmit(selection);
      // onClose();
    } catch (error) {
      console.error('Failed to apply date selection:', error);
    // } finally {
    //   setIsLoading(false);
    }
  };

  const formatDateRange = (range) => {
    if (range?.startDate) {
      const startDate = parseISO(range.startDate);
      const endDate = parseISO(range.endDate) || startDate;
      const days = differenceInDays(endDate, startDate) + 1;
      return `${format(startDate, 'MMM dd, yyyy')} - ${format(endDate, 'MMM dd, yyyy')} (${days} day${days > 1 ? 's' : ''})`;
    }
    return 'No date selected';
  };

  const isValidSelection = () => {
    return dateRange?.from && dateRange?.to && 
           (dateRange.from instanceof Date || typeof dateRange.from === 'string') &&
           (dateRange.to instanceof Date || typeof dateRange.to === 'string');
  };

  const getDaysInSelection = () => {
    if (!isValidSelection()) return 0;
    return differenceInDays(dateRange.to, dateRange.from) + 1;
  };

  const presetGroups = [
    {
      title: 'MOST COMMON',
      options: [
        { key: 'weekToDate', label: 'Week to Date' },
        { key: 'monthToDate', label: 'Month to Date' },
        { key: 'yesterday', label: 'Yesterday' },
      ]
    },
    {
      title: 'ROLLING PERIODS',
      options: [
        { key: 'last7Days', label: 'Last 7 Days' },
        { key: 'last30Days', label: 'Last 30 Days' }
      ]
    },
    {
      title: 'COMPLETE PERIODS',
      options: [
        { key: 'lastWeek', label: 'Last Week' },
        { key: 'lastMonth', label: 'Last Month' }
      ]
    },
    {
      title: 'CUSTOM',
      options: [
        { key: 'custom', label: 'Custom Range' }
      ]
    }
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarIcon sx={{ color: '#7C3AED' }} />
            <Typography variant="h6" fontWeight="medium">
              Select Date Range
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          {/* Current Report Range */}
          <Paper 
            variant="outlined" 
            sx={{ 
              py: 1,
              px: 2, 
              mb: 3,
              mt: 1, 
              backgroundColor: 'grey.50',
              border: '1px solid',
              borderColor: 'grey.200',
              borderRadius: 2
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <ClockIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" fontWeight="medium" color="text.secondary">
                Current Report Range
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {formatDateRange(currentDateRange)}
            </Typography>
          </Paper>

          {/* Quick Select */}
          <Box mb={0.5}>
            <Typography variant="body2" fontWeight="medium" mb={1}>
              Quick Select
            </Typography>

            {presetGroups.map((group, groupIndex) => (
              <Box key={group.title} ml={2}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 'medium',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase'
                  }}
                >
                  {group.title}
                </Typography>
                <Grid container spacing={0}>
                  {group.options.map((option) => {
                    const isSelected = selectedPreset === option.key;
                    return (
                      <Grid item xs={6} key={option.key}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            py: 0.5,
                          }}
                          onClick={() => handlePresetSelect(option.key)}
                        >
                          <Radio
                            checked={isSelected}
                            size="small"
                            sx={{
                              p: 0,
                              mr: 1,
                              color: '#E5E7EB',
                              '&.Mui-checked': {
                                color: '#7C3AED',
                              },
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{ color: isSelected ? '#7C3AED' : 'white' }}
                            fontWeight={isSelected ? 600 : 400}
                          >
                            {option.label}
                          </Typography>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
                {groupIndex === presetGroups.length - 2 && <Divider sx={{ mt: 1 }} />}
              </Box>
            ))}
          </Box>

          {/* Custom Date Selection */}
          {selectedPreset === 'custom' && (
            <Box mb={3}>
              <Typography variant="body2" fontWeight="medium" mb={1}>
                Select Custom Date Range
              </Typography>
              <Stack
                direction='column'
                spacing={0.5}
                alignItems='center'
                justifyContent='center'
                mx={2}
              >
                <MDDatePicker 
                  id="custom-date-range"
                  value={dateRange.from && dateRange.to ? [dateRange.from, dateRange.to] : null}
                  options={{
                    mode: 'range',
                    minDate: dateConstraints.minDate,
                    maxDate: dateConstraints.maxDate,
                    'locale': {
                      'firstDayOfWeek': 1
                    }
                  }}
                  fullWidth
                  onChange={range => handleCustomDateRangeChange(range)}
                />
              </Stack>
            </Box>
          )}

          {/* New Selection Preview */}
          {isValidSelection() && (
            <Paper
              sx={{
                px: 3,
                py: 1.5,
                mt: 2,
                border: '2px solid',
                borderColor: '#7C3AED',
                backgroundColor: 'rgba(124, 58, 237, 0.04)',
                borderRadius: 2
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <DateRangeIcon sx={{ color: '#7C3AED', fontSize: 16 }} />
                <Typography variant="body2" fontWeight="medium" color="#7C3AED">
                  New Selection Preview
                </Typography>
              </Box>
              <Typography variant="h5" fontWeight="bold" color="white" mb={0.5}>
                {dateRange.from && format(dateRange.from, 'MMM dd')} -{' '}
                {format(dateRange.to || dateRange.from, 'MMM dd, yyyy')}
              </Typography>
              <Typography variant="body2" sx={{ color: '#7C3AED' }}>
                {dateRange.from && dateRange.to
                  ? `${differenceInDays(dateRange.to, dateRange.from) + 1} days of data`
                  : '1 day of data'}
              </Typography>
            </Paper>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <AdminButton variant="text" size="small" onClick={onClose} disabled={isLoading}>
            Cancel
          </AdminButton>
          <AdminButton
            variant="contained"
            size="small"
            onClick={handleApply}
            disabled={!isValidSelection()}
          >
            Apply
          </AdminButton>
        </DialogActions>
      </Dialog>
  );
};

export default DateRangeSelectionModal;